// The field's double-buffered frame swap. Talks to a `FieldHost` port rather than
// to `map`, which is what makes it testable — crossfade.test.ts drives a fake host
// with a scripted clock and asserts every invariant below.
//
// ── why a new source per frame ───────────────────────────────────────────────
// Each frame gets its OWN source+layer rather than repointing a shared one. That is
// the whole trick: after setTiles() on a reused source, MapLibre still reports the
// *previous* frame as loaded (isSourceLoaded and querySourceFeatures both answer from
// the old tiles), so there is no reliable way to ask "is the new frame ready?" — and
// revealing on that stale answer is what made days flicker back and forth. A brand new
// source has no old tiles to lie about: it starts genuinely unloaded, and only reports
// loaded once every tile for the viewport is in.
//
// Two stacked translucent fills composite as 1-(1-a)(1-b), which is why a crossfade
// eases the outgoing frame along a COMPENSATING curve rather than a linear one:
// holding the old at full pulses once per frame, easing both linearly dips through
// the dark base. The compensated pair holds combined coverage flat for the whole fade.

export const FIELD_FADE_MS = 500;

// How a finished frame is revealed.
//
//   'crossfade'  the default for every data swap — new frame fades in above the old
//                while the old eases down the compensating curve
//   'fadein'     colours rise from black, no old frame beneath: the first paint, and
//                (with fadeOutCurrent) a variable switch
//   'instant'    old dropped in the same render the new appears; direct-control paths
export type FieldRevealMode = 'crossfade' | 'fadein' | 'instant';

// One frame's source+layer pair, plus (while it is loading) the disposer for the
// listener watching it and the tile URL it was built from.
export interface FieldFrame {
  sourceId: string;
  layerId: string;
  url?: string;
  dispose?: () => void;
}

// Everything the machine needs from the map, and nothing else. Narrow on purpose:
// this is the whole surface a test has to fake.
export interface FieldHost {
  // Add the source and its layer, the layer inserted beneath the basemap water /
  // ocean mask / land outline, starting at zero opacity.
  addFrame(sourceId: string, layerId: string, url: string): void;
  removeLayer(layerId: string): void;
  removeSource(sourceId: string): void;
  hasLayer(layerId: string): boolean;
  hasSource(sourceId: string): boolean;
  getOpacity(layerId: string): number | null;
  setOpacity(layerId: string, value: number): void;
  setOpacityTransition(layerId: string, ms: number): void;
  setVisibility(layerId: string, visible: boolean): void;
  setColor(layerId: string, color: unknown): void;
  // Is every tile this source needs for the viewport in, WITH real geometry?
  isReady(sourceId: string): boolean;
  // Call `fn` whenever this source reports new data. Returns an unsubscribe.
  watchSource(sourceId: string, fn: () => void): () => void;
  raf(fn: (now: number) => void): number;
  cancelRaf(handle: number): void;
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(handle: unknown): void;
}

// The bits of app state the machine reads each time it needs them — live, so the
// opacity slider and the field toggle stay responsive mid-fade.
export interface FieldSettings {
  enabled(): boolean;
  opacity(): number;
  // The tile URL for the frame that should be showing right now.
  url(): string;
}

export interface FieldMachine {
  // Advance to the frame `settings.url()` names, without flicker.
  update(mode?: FieldRevealMode): void;
  // Ease the on-screen frame out to empty and let go of it, so the next frame
  // fades in from black — for switching variable, where cross-fading one metric's
  // colours into another's would show wrong-for-its-data colours in between.
  fadeOutCurrent(): void;
  // Every field layer currently in the style (current + any in-flight frame).
  layerIds(): string[];
  // Direct control: respond now, don't inherit a lingering fade transition.
  setVisible(on: boolean): void;
  setOpacity(value: number): void;
  setColor(color: unknown): void;
  // The frame on screen, or null. Exposed for the callers that need to know
  // whether there is anything to act on.
  current(): FieldFrame | null;
}

export function createFieldMachine(host: FieldHost, settings: FieldSettings): FieldMachine {
  let seq = 0;
  let current: FieldFrame | null = null; // on screen
  let pending: FieldFrame | null = null; // loading, still invisible
  let prev: FieldFrame | null = null; // kept under the current one for the length of a cross-fade
  let xfadeRaf: number | null = null; // rAF handle driving that cross-fade
  let fadingOut: FieldFrame | null = null; // frame easing to empty on a variable switch
  let fadingOutTimer: unknown = null;
  let desiredUrl: string | null = null; // newest frame requested while one is already loading
  let desiredMode: FieldRevealMode | null = null; // reveal mode to carry into that queued frame

  // Default reveal: cross-fade, so every data swap morphs — playback, scrubbing, a
  // peaks-rail pick, a bucket change. Rapid swaps stay responsive because an
  // interrupted fade hands its actual opacity to the next one (see startFade);
  // callers pass 'fadein' where the old colours were cleared first.
  const defaultMode = (): FieldRevealMode => 'crossfade';

  function removeFrame(frame: FieldFrame) {
    // Pairs the listener's life to the frame's. finish() already unbinds on the happy
    // path; this is what keeps a frame dropped before it ever finished from leaving a
    // listener bound to a source that no longer exists.
    if (frame.dispose) frame.dispose();
    if (host.hasLayer(frame.layerId)) host.removeLayer(frame.layerId);
    if (host.hasSource(frame.sourceId)) host.removeSource(frame.sourceId);
  }

  // Drop the frame left underneath the current one after a cross-fade. Idempotent, so
  // calling it early (because a newer frame is arriving) is safe — the interrupted
  // fade's top layer keeps its partial opacity and the next fade picks up from there.
  function dropPrev() {
    if (xfadeRaf != null) {
      host.cancelRaf(xfadeRaf);
      xfadeRaf = null;
    }
    if (prev) {
      removeFrame(prev);
      prev = null;
    }
  }

  // Fade the new frame in — over the old when there is one (cross-fade), from empty
  // when there is not (fade-in). Driven manually because MapLibre can only ease each
  // layer independently: with the new frame at a, the frame underneath must sit at
  // b = o0(1-e)/(1-a) — then 1-(1-a)(1-b) stays flat from o0 to the target rather than
  // pulsing above it (old held solid) or dipping below it (both fading linearly).
  function startFade(newLayerId: string, oldFrame: FieldFrame | null = null) {
    prev = oldFrame;
    // Start from the old frame's ACTUAL opacity: under fast playback it may itself be
    // mid-fade, and picking up from there keeps the interruption invisible.
    const cur = oldFrame && host.hasLayer(oldFrame.layerId) ? host.getOpacity(oldFrame.layerId) : null;
    const o0 = typeof cur === 'number' ? cur : settings.opacity();
    // The clock starts on the first rAF tick, not now: at reveal time the main thread
    // can still be busy uploading the frame's tiles (worst on the very first load), and
    // a clock started here would burn the whole fade before anything renders.
    let t0: number | null = null;
    const step = (now: number) => {
      xfadeRaf = null;
      if (prev !== oldFrame) return; // cancelled or superseded
      if (!current || current.layerId !== newLayerId) return;
      if (t0 == null) t0 = now;
      // Read the target each tick so the opacity slider / field toggle stay live mid-fade.
      const target = settings.enabled() ? settings.opacity() : 0;
      const p = Math.min(1, (now - t0) / FIELD_FADE_MS);
      const e = p * p * (3 - 2 * p); // smoothstep — same feel as MapLibre's own ease
      const a = target * e;
      if (host.hasLayer(newLayerId)) host.setOpacity(newLayerId, a);
      if (oldFrame && host.hasLayer(oldFrame.layerId)) {
        const b = 1 - a > 1e-3 ? (o0 * (1 - e)) / (1 - a) : 0;
        host.setOpacity(oldFrame.layerId, b);
      }
      if (p < 1) xfadeRaf = host.raf(step);
      else dropPrev();
    };
    xfadeRaf = host.raf(step);
  }

  // Show the finished frame and retire the previous one per the reveal mode.
  function reveal(sourceId: string, layerId: string, url: string, mode: FieldRevealMode = 'instant') {
    const old = current;
    // The very first paint has nothing to swap from, so ease it up from empty rather
    // than popping — an instant request becomes a fade-in when there is no old frame.
    if (mode === 'instant' && !old) mode = 'fadein';

    // A frame still lingering under an earlier cross-fade is now (at least mostly)
    // hidden by the frame that replaced it — drop it before this one takes its place.
    dropPrev();

    current = { sourceId, layerId, url };
    pending = null;

    if (settings.enabled() && mode !== 'instant') {
      // 'fadein' means the old colours were already cleared (fadeOutCurrent), so
      // any old still here just goes; 'crossfade' hands it to the rAF loop, which
      // raises the new frame from the 0 the layer spec starts at and retires the old
      // itself once the fade lands.
      if (mode === 'fadein' && old) removeFrame(old);
      startFade(layerId, mode === 'crossfade' ? old : null);
    } else {
      if (settings.enabled()) {
        host.setOpacityTransition(layerId, 0);
        host.setOpacity(layerId, settings.opacity());
      }
      // Instant (or field hidden): the swap lands in one render.
      if (old) removeFrame(old);
    }

    // Catch up to the newest day requested while this one was loading.
    const next = desiredUrl;
    const nextMode = desiredMode;
    desiredUrl = null;
    desiredMode = null;
    if (next && next !== url) load(next, nextMode || defaultMode());
  }

  function load(url: string, mode: FieldRevealMode = defaultMode()) {
    const n = ++seq;
    const sourceId = `field-src-${n}`;
    const layerId = `field-fill-${n}`;

    host.addFrame(sourceId, layerId, url);

    const frame: FieldFrame = { sourceId, layerId, url };
    const finish = () => {
      if (!pending || pending.layerId !== layerId) return;
      if (!host.isReady(sourceId)) return;
      if (frame.dispose) frame.dispose();
      frame.dispose = undefined;
      reveal(sourceId, layerId, url, mode);
    };
    frame.dispose = host.watchSource(sourceId, finish);
    pending = frame;
  }

  // Every field layer currently in the style (current + any in-flight frame).
  const layerIds = () => ([current, pending].filter(Boolean) as FieldFrame[]).map((f) => f.layerId);

  return {
    update(mode: FieldRevealMode = defaultMode()) {
      const url = settings.url();
      // One load at a time. A newer frame just replaces the queued one, so fast playback
      // skips ahead to the latest day instead of queueing up a backlog.
      if (pending) {
        desiredUrl = url;
        desiredMode = mode;
        return;
      }
      load(url, mode);
    },

    fadeOutCurrent() {
      // Stop any cross-fade still driving this frame's opacity — the rAF loop would
      // otherwise fight the fade-out below — and drop the frame beneath it.
      dropPrev();
      const old = current;
      if (!old) return;
      current = null;
      // Only one fade-out at a time; a rapid second switch drops the first outright.
      if (fadingOut) {
        if (fadingOutTimer != null) host.clearTimer(fadingOutTimer);
        removeFrame(fadingOut);
      }
      fadingOut = old;
      if (host.hasLayer(old.layerId)) {
        host.setOpacityTransition(old.layerId, FIELD_FADE_MS);
        host.setOpacity(old.layerId, 0);
      }
      fadingOutTimer = host.setTimer(() => {
        fadingOutTimer = null;
        if (fadingOut === old) fadingOut = null;
        removeFrame(old);
      }, FIELD_FADE_MS);
    },

    layerIds,

    setVisible(on: boolean) {
      for (const id of layerIds()) {
        if (host.hasLayer(id)) host.setVisibility(id, on);
      }
      if (current && host.hasLayer(current.layerId)) {
        host.setOpacityTransition(current.layerId, 0);
        host.setOpacity(current.layerId, on ? settings.opacity() : 0);
      }
    },

    setOpacity(value: number) {
      if (settings.enabled() && current && host.hasLayer(current.layerId)) {
        // Track the slider instantly rather than easing after every step.
        host.setOpacityTransition(current.layerId, 0);
        host.setOpacity(current.layerId, value);
      }
    },

    setColor(color: unknown) {
      for (const id of layerIds()) {
        if (host.hasLayer(id)) host.setColor(id, color);
      }
    },

    current() {
      return current;
    },
  };
}
